import styled from 'styled-components';

export const OrgSettingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 40px 48px;
  flex: 1;
  overflow-y: auto;
  background: ${({ theme }) => theme.colors.neutral.primary[10]};
`;

export const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutral.primary[100]};
  margin: 0 0 8px 0;
`;

export const PageSubtitle = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.neutral.primary[70]};
  margin: 0 0 32px 0;
  max-width: 560px;
  line-height: 1.6;
`;

export const SectionTitle = styled.h2`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutral.primary[60]};
  margin: 0 0 12px 0;
  text-transform: uppercase;
  letter-spacing: 0.07em;
`;

/* ── Wizard card ─────────────────────────────────────────── */

export const WizardCard = styled.div`
  background: #ffffff;
  border: 1px solid ${({ theme }) => theme.colors.neutral.primary[30]};
  border-radius: 10px;
  padding: 28px;
  max-width: 560px;
  display: flex;
  flex-direction: column;
  gap: 22px;
`;

export const StepHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const StepIconWrapper = styled.div<{ variant: 'neutral' | 'success' | 'active' }>`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 17px;
  background: ${({ variant, theme }) => {
    if (variant === 'success') return theme.colors.accent.green[10];
    if (variant === 'active') return theme.colors.accent.blue[10];
    return theme.colors.neutral.primary[5];
  }};
  color: ${({ variant, theme }) => {
    if (variant === 'success') return theme.colors.accent.green[100];
    if (variant === 'active') return theme.colors.accent.blue[100];
    return theme.colors.neutral.primary[70];
  }};
  border: ${({ variant, theme }) => {
    if (variant === 'success') return `1px solid ${theme.colors.accent.green[30]}`;
    if (variant === 'active') return `1px solid ${theme.colors.accent.blue[30]}`;
    return `1px solid ${theme.colors.neutral.primary[30]}`;
  }};
`;

export const StepTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const StepTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutral.primary[100]};
  margin: 0;
`;

export const StepSubtitle = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.neutral.primary[60]};
`;

export const StepDescription = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.neutral.primary[80]};
  margin: 0;
  line-height: 1.6;
`;

export const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.neutral.primary[20]};
  margin: 0;
`;

/* ── Field / input ───────────────────────────────────────── */

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const FieldLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutral.primary[80]};
`;

export const InputRow = styled.div`
  display: flex;
  gap: 10px;
`;

export const TenantInput = styled.input`
  flex: 1;
  height: 38px;
  padding: 0 12px;
  font-size: 14px;
  border: 1px solid ${({ theme }) => theme.colors.neutral.primary[30]};
  border-radius: 6px;
  outline: none;
  color: ${({ theme }) => theme.colors.neutral.primary[100]};
  background: #fff;
  transition: border-color 0.15s;

  &::placeholder {
    color: ${({ theme }) => theme.colors.neutral.primary[40]};
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.accent.blue[70]};
  }
`;

export const FieldHelp = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.neutral.primary[60]};
  line-height: 1.5;
`;

/* ── Tenant value (read-only) ────────────────────────────── */

export const TenantValueBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

export const TenantValueLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.colors.neutral.primary[60]};
`;

export const TenantValue = styled.span`
  font-size: 13px;
  font-family: 'SFMono-Regular', 'Consolas', 'Liberation Mono', monospace;
  color: ${({ theme }) => theme.colors.neutral.primary[100]};
  background: ${({ theme }) => theme.colors.neutral.primary[5]};
  border: 1px solid ${({ theme }) => theme.colors.neutral.primary[20]};
  border-radius: 5px;
  padding: 7px 11px;
  display: inline-block;
  word-break: break-all;
`;

/* ── Buttons ─────────────────────────────────────────────── */

export const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const BaseButton = styled.button`
  padding: 9px 20px;
  font-size: 13px;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const PrimaryButton = styled(BaseButton)`
  background: ${({ theme }) => theme.colors.accent.blue[100]};
  color: #ffffff;
  border: 1px solid ${({ theme }) => theme.colors.accent.blue[100]};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.accent.blue[70]};
    border-color: ${({ theme }) => theme.colors.accent.blue[70]};
  }
`;

export const DangerButton = styled(BaseButton)`
  background: ${({ theme }) => theme.colors.accent.red[10]};
  color: ${({ theme }) => theme.colors.accent.red[100]};
  border: 1px solid ${({ theme }) => theme.colors.accent.red[30]};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.accent.red[30]};
  }
`;

export const GhostButton = styled(BaseButton)`
  background: transparent;
  color: ${({ theme }) => theme.colors.neutral.primary[70]};
  border: 1px solid ${({ theme }) => theme.colors.neutral.primary[30]};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.neutral.primary[10]};
  }
`;

/* ── Admin consent block ─────────────────────────────────── */

export const AdminConsentBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: ${({ theme }) => theme.colors.accent.blue[10]};
  border: 1px solid ${({ theme }) => theme.colors.accent.blue[30]};
  border-radius: 6px;
  padding: 14px 16px;
`;

export const AdminConsentLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.colors.accent.blue[100]};
`;

export const AdminConsentDescription = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.neutral.primary[80]};
  margin: 0;
  line-height: 1.5;
`;

export const AdminConsentUrlRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const AdminConsentUrl = styled.span`
  font-size: 12px;
  font-family: 'SFMono-Regular', 'Consolas', 'Liberation Mono', monospace;
  color: ${({ theme }) => theme.colors.accent.blue[100]};
  background: #ffffff;
  border: 1px solid ${({ theme }) => theme.colors.accent.blue[30]};
  border-radius: 5px;
  padding: 6px 10px;
  flex: 1;
  word-break: break-all;
`;

export const CopyButton = styled(BaseButton)`
  background: transparent;
  color: ${({ theme }) => theme.colors.accent.blue[100]};
  border: 1px solid ${({ theme }) => theme.colors.accent.blue[30]};
  padding: 6px 14px;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.accent.blue[10]};
  }
`;

/* ── Messages ────────────────────────────────────────────── */

export const ErrorMessage = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.accent.red[100]};
  margin: 0;
  line-height: 1.5;
`;
